import { useState, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Outlet, useLocation, useNavigate, Navigate, useParams } from "react-router-dom";
import { useEffect, useRef } from "react";
import { Loader2 } from "lucide-react";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Navbar } from "@/components/layout/Navbar";
import { Footer, DisclaimerBanner } from "@/components/layout/Footer";
import { AuthDialog } from "@/components/AuthDialog";
import { AIChatWidget } from "@/components/AIChatWidget";
import { Toaster } from "@/components/ui/sonner";

const Home = lazy(() => import("@/pages/Home"));
const LegalTips = lazy(() => import("@/pages/LegalTips"));
const StateLaws = lazy(() => import("@/pages/StateLaws"));
const Attorneys = lazy(() => import("@/pages/Attorneys"));
const Community = lazy(() => import("@/pages/Community"));
const ThreadDetail = lazy(() => import("@/pages/ThreadDetail"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const Resources = lazy(() => import("@/pages/Resources"));
const About = lazy(() => import("@/pages/About"));
const Contact = lazy(() => import("@/pages/Contact"));
const ReachOut = lazy(() => import("@/pages/ReachOut"));
const ConsultationGuide = lazy(() => import("@/pages/ConsultationGuide"));
const ConsultationChecklist = lazy(() => import("@/pages/ConsultationChecklist"));
const HearingGuide = lazy(() => import("@/pages/HearingGuide"));
const HearingChecklist = lazy(() => import("@/pages/HearingChecklist"));
const Unsubscribe = lazy(() => import("@/pages/Unsubscribe"));
const MediationGuide = lazy(() => import("@/pages/MediationGuide"));
const MediationChecklist = lazy(() => import("@/pages/MediationChecklist"));
const UtahChecklist = lazy(() => import("@/pages/UtahChecklist"));
const LegalDisclaimer = lazy(() => import("@/pages/LegalDisclaimer"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const CaseJournal = lazy(() => import("@/pages/CaseJournal"));
const Captains = lazy(() => import("@/pages/Captains"));
const LegalDocuments = lazy(() => import("@/pages/LegalDocuments"));
const StateDetail = lazy(() => import("@/pages/StateDetail"));
const ArticlePage = lazy(() => import("@/pages/ArticlePage"));
const StatesIndex = lazy(() => import("@/pages/StatesIndex"));
const SearchPage = lazy(() => import("@/pages/StatesIndex").then((m) => ({ default: m.SearchPage })));
const AdminSeo = lazy(() => import("@/pages/AdminSeo"));

const EDITORIAL_STATES = ["utah", "texas"];
function StateRoute() {
  const { slug } = useParams();
  return EDITORIAL_STATES.includes(slug) ? <ArticlePage slugOverride={`states/${slug}`} /> : <StateDetail />;
}
function LegacyStateRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/states/${slug}`} replace />;
}
const PaymentSuccess = lazy(() => import("@/pages/PaymentResult"));
const PaymentCancel = lazy(() => import("@/pages/PaymentResult").then((m) => ({ default: m.PaymentCancel })));
const AdminInbox = lazy(() => import("@/pages/AdminInbox"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const ResearchHistory = lazy(() => import("@/pages/ResearchHistory"));
import { BookmarksProvider } from "@/context/BookmarksContext";
import { logError } from "@/lib/log";

function ScrollToTop() {
  const { pathname, hash, search } = useLocation();
  useEffect(() => {
    if (typeof window.gtag === "function") {
      window.gtag("event", "page_view", { page_path: pathname + search, page_location: window.location.href, page_title: document.title });
    }
  }, [pathname, search]);
  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) { setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 150); return; }
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

function Layout() {
  const [authOpen, setAuthOpen] = useState(false);
  const openAuth = () => setAuthOpen(true);
  return (
    <div className="min-h-screen bg-[#0B111E] flex flex-col">
      <ScrollToTop />
      <Navbar onOpenAuth={openAuth} />
      <div className="pt-16"><DisclaimerBanner /></div>
      <main className="flex-1">
        <Suspense fallback={<div className="min-h-[60vh] flex items-center justify-center" data-testid="page-loading"><Loader2 className="h-8 w-8 animate-spin text-amber-400" /></div>}>
          <Outlet context={{ openAuth }} />
        </Suspense>
      </main>
      <Footer />
      <AIChatWidget onRequireAuth={openAuth} />
      <AuthDialog open={authOpen} onOpenChange={setAuthOpen} />
      <Toaster position="top-center" richColors theme="dark" />
    </div>
  );
}

function AuthCallback() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const sid = new URLSearchParams(window.location.hash.replace(/^#/, "")).get("session_id");
    if (!sid) { navigate("/", { replace: true }); return; }
    api
      .post("/auth/google/session", { session_id: sid })
      .then((res) => setUser(res.data.user))
      .catch((e) => logError("Google session exchange failed", e))
      .finally(() => {
        window.history.replaceState(null, "", window.location.pathname);
        navigate("/community", { replace: true });
      });
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#0B111E] text-slate-300 gap-3" data-testid="auth-callback">
      <Loader2 className="h-7 w-7 animate-spin text-amber-400" />
      <p className="text-sm">Signing you in…</p>
    </div>
  );
}

function RootRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route element={<Layout />}>
        <Route path="/" element={<Home />} />
        <Route path="/legal-tips" element={<LegalTips />} />
        <Route path="/legal-tips/:id" element={<LegalTips />} />
        <Route path="/state-laws" element={<StateLaws />} />
        <Route path="/state-laws/:slug" element={<LegacyStateRedirect />} />
        <Route path="/states" element={<StatesIndex />} />
        <Route path="/states/:slug" element={<StateRoute />} />
        <Route path="/states/utah/checklist" element={<UtahChecklist />} />
        <Route path="/states/:slug/:sub" element={<ArticlePage />} />
        <Route path="/search" element={<SearchPage />} />
        <Route path="/admin/seo" element={<AdminSeo />} />
        <Route path="/resources/directory" element={<Resources />} />
        <Route path="/attorneys" element={<Attorneys />} />
        <Route path="/community" element={<Community />} />
        <Route path="/community/thread/:id" element={<ThreadDetail />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/resources" element={<ArticlePage slugOverride="resources" />} />
        <Route path="/resources/:sub" element={<ArticlePage />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/reach-out" element={<ReachOut />} />
        <Route path="/resources/first-custody-attorney-consultation" element={<ConsultationGuide />} />
        <Route path="/resources/first-custody-attorney-consultation/checklist" element={<ConsultationChecklist />} />
        <Route path="/resources/first-custody-hearing" element={<HearingGuide />} />
        <Route path="/resources/first-custody-hearing/checklist" element={<HearingChecklist />} />
        <Route path="/unsubscribe" element={<Unsubscribe />} />
        <Route path="/resources/first-custody-mediation" element={<MediationGuide />} />
        <Route path="/resources/first-custody-mediation/checklist" element={<MediationChecklist />} />
        <Route path="/legal-disclaimer" element={<LegalDisclaimer />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/journal" element={<CaseJournal />} />
        <Route path="/captains" element={<Captains />} />
        <Route path="/documents" element={<LegalDocuments />} />
        <Route path="/documents/:id" element={<LegalDocuments />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
        <Route path="/admin" element={<AdminInbox />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/research" element={<ResearchHistory />} />
        <Route path="*" element={<ArticlePage />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BookmarksProvider>
          <BrowserRouter>
            <RootRouter />
          </BrowserRouter>
        </BookmarksProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
